import { createSlice, createAsyncThunk, createSelector } from '@reduxjs/toolkit';

import { addMarketplaceEntities, getListingsById } from '../../ducks/marketplaceData.duck';
import { hydrateCart } from '../../ducks/cart.duck';
import { storableError } from '../../util/errors';
import { createImageVariantConfig } from '../../util/sdkLoader';
import { listingPurchaseAvailability } from '../../util/api';
import { applyPurchaseAvailabilityToListings } from '../../util/purchaseAvailability';

const RESULT_PAGE_SIZE = 50;

const resultIds = data => {
  const listings = data?.data || [];
  return listings.map(l => l.id);
};

const toListingIdRef = idLike => (typeof idLike === 'string' ? { uuid: idLike } : idLike);

export const fetchListingsByIdsThunk = createAsyncThunk(
  'CartPage/fetchListingsByIds',
  async ({ ids = [], config, cachedListings = null }, thunkAPI) => {
    const { dispatch, extra: sdk, rejectWithValue } = thunkAPI;

    if (!ids.length) {
      return { data: { data: [], meta: null } };
    }

    const { aspectWidth = 1, aspectHeight = 1, variantPrefix = 'listing-card' } =
      config?.layout?.listingImage || {};
    const aspectRatio = aspectHeight / aspectWidth;

    const queryParams = {
      ids,
      perPage: RESULT_PAGE_SIZE,
      include: ['author', 'images', 'currentStock'],
      'fields.listing': [
        'title',
        'description',
        'price',
        'metadata',
        'publicData',
        'state',
        'deleted',
        'publicData.listingType',
        'publicData.transactionProcessAlias',
        'publicData.unitType',
      ],
      'fields.user': ['profile.displayName', 'profile.abbreviatedName', 'profile.publicData'],
      'fields.image': [
        `variants.${variantPrefix}`,
        `variants.${variantPrefix}-2x`,
        'variants.scaled-small',
        'variants.scaled-medium',
        'variants.scaled-large',
      ],
      ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
      ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
      'limit.images': 1,
    };

    try {
      const response = Array.isArray(cachedListings)
        ? { data: { data: cachedListings, meta: null } }
        : await sdk.listings.query(queryParams);
      const fetchedListingIds = (response?.data?.data || [])
        .map(listing => listing?.id?.uuid)
        .filter(Boolean);
      const availability = fetchedListingIds.length
        ? await listingPurchaseAvailability({ listingIds: fetchedListingIds })
        : { availabilityByListingId: {} };
      if (response?.data?.data) {
        response.data.data = applyPurchaseAvailabilityToListings(response.data.data, availability);
      }
      dispatch(addMarketplaceEntities(response));
      return response;
    } catch (e) {
      return rejectWithValue(storableError(e));
    }
  }
);

const cartPageSlice = createSlice({
  name: 'CartPage',
  initialState: {
    fetchListingInProgress: false,
    fetchListingError: null,
    currentPageResultIds: [],
    pagination: null,
    fetchedIdsKey: null,
  },
  reducers: {
    clearListings: state => {
      state.fetchListingInProgress = false;
      state.fetchListingError = null;
      state.currentPageResultIds = [];
      state.pagination = null;
      state.fetchedIdsKey = null;
    },
    setCurrentListings: (state, action) => {
      const ids = (action.payload?.ids || []).map(toListingIdRef);

      state.fetchListingInProgress = false;
      state.fetchListingError = null;
      state.currentPageResultIds = ids;
      state.fetchedIdsKey = ids
        .map(id => id?.uuid || null)
        .filter(Boolean)
        .sort()
        .join(',');
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchListingsByIdsThunk.pending, state => {
        state.fetchListingInProgress = true;
        state.fetchListingError = null;
      })
      .addCase(fetchListingsByIdsThunk.fulfilled, (state, action) => {
        state.fetchListingInProgress = false;
        state.currentPageResultIds = resultIds(action.payload?.data);
        state.pagination = action.payload?.data?.meta || null;
        state.fetchedIdsKey = (action.meta?.arg?.ids || [])
          .slice()
          .sort()
          .join(',');
      })
      .addCase(fetchListingsByIdsThunk.rejected, (state, action) => {
        state.fetchListingInProgress = false;
        state.fetchListingError = action.payload || action.error;
      });
  },
});

export const { clearListings, setCurrentListings } = cartPageSlice.actions;

export default cartPageSlice.reducer;

const selectCartPageState = state => state.CartPage || {};
const selectMarketplaceData = state => state.marketplaceData;
const selectCurrentPageResultIds = createSelector(
  [selectCartPageState],
  cartPage => cartPage.currentPageResultIds || []
);
const selectCartPageListings = createSelector(
  [selectMarketplaceData, selectCurrentPageResultIds],
  (marketplaceData, currentPageResultIds) =>
    getListingsById({ marketplaceData }, currentPageResultIds || [])
);

export const cartPageSelector = createSelector(
  [selectCartPageState, selectCurrentPageResultIds, selectCartPageListings],
  (cartPage, currentPageResultIds, listings) => ({
    fetchListingInProgress: cartPage.fetchListingInProgress,
    fetchListingError: cartPage.fetchListingError,
    currentPageResultIds,
    pagination: cartPage.pagination,
    fetchedIdsKey: cartPage.fetchedIdsKey || null,
    listings,
  })
);

export const fetchListingsByIds = ({ ids = [], config }) => (dispatch, getState) => {
  const normalizedIds = Array.from(new Set((ids || []).filter(Boolean)));
  if (!normalizedIds.length) {
    dispatch(clearListings());
    return Promise.resolve();
  }

  const state = getState();
  const requestedResultIds = normalizedIds.map(toListingIdRef);
  const cachedListings = getListingsById(
    { marketplaceData: state.marketplaceData },
    requestedResultIds
  );
  const cachedListingIds = new Set(
    (cachedListings || []).map(listing => listing?.id?.uuid).filter(Boolean)
  );
  const hasAllRequestedListingsCached = normalizedIds.every(listingId =>
    cachedListingIds.has(listingId)
  );
  const idsKey = normalizedIds
    .slice()
    .sort()
    .join(',');
  const hasPreviouslyFetchedRequestedIds = state?.CartPage?.fetchedIdsKey === idsKey;

  // Always revalidate the seller state, while retaining already-hydrated listing data when
  // possible. A cart may have been cached before the seller enabled Vacation Mode.
  return dispatch(
    fetchListingsByIdsThunk({
      ids: normalizedIds,
      config,
      cachedListings:
        hasAllRequestedListingsCached || hasPreviouslyFetchedRequestedIds ? cachedListings : null,
    })
  );
};

export const loadData = (params, search, config) => async (dispatch, getState) => {
  await dispatch(hydrateCart());
  const ids = Object.keys(getState()?.cart?.items || {});
  return dispatch(fetchListingsByIds({ ids, config }));
};
